import { useEffect, useMemo, useState, type FormEvent } from "react";
import {
  fetchTransporterEligibleDispatchRuns,
  fetchTransporterFleetStatus,
  postTransporterManualAssign,
  type TransporterEligibleDispatchRunDto,
  type TransporterFleetDriverDto,
} from "../lib/fetch-transporter-delivery-ops";
import { useTransporterWorkspace } from "../workspace/transporter-workspace-context";

function runLabel(r: TransporterEligibleDispatchRunDto): string {
  return `${r.dispensaryLegalName} · ${r.driverFullName}`;
}

export function ManualDispatchScreen() {
  const { transporterId, getToken } = useTransporterWorkspace();
  const [runs, setRuns] = useState<TransporterEligibleDispatchRunDto[] | null>(null);
  const [drivers, setDrivers] = useState<TransporterFleetDriverDto[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [selectedRunId, setSelectedRunId] = useState("");
  const [selectedDriverId, setSelectedDriverId] = useState("");
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitOk, setSubmitOk] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const driversOnShift = useMemo(
    () => (drivers ?? []).filter((d) => d.onShift && d.isActive),
    [drivers],
  );

  useEffect(() => {
    if (!transporterId) {
      setRuns(null);
      setDrivers(null);
      setLoadError(null);
      return;
    }
    let cancelled = false;
    setLoadError(null);
    void (async () => {
      try {
        const [r, d] = await Promise.all([
          fetchTransporterEligibleDispatchRuns(getToken, transporterId),
          fetchTransporterFleetStatus(getToken, transporterId),
        ]);
        if (!cancelled) {
          setRuns(r.runs);
          setDrivers(d.drivers);
          setSelectedRunId("");
          setSelectedDriverId("");
        }
      } catch (e) {
        if (!cancelled) {
          setRuns(null);
          setDrivers(null);
          setLoadError(e instanceof Error ? e.message : "dispatch_load_failed");
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [transporterId, getToken]);

  if (!transporterId) {
    return (
      <section className="space-y-2">
        <p className="text-sm text-gray">
          Choose a transporter in the scope panel (platform admin) or use a linked dispatcher account.
        </p>
      </section>
    );
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    const tid = transporterId;
    if (!tid) {
      return;
    }
    setSubmitError(null);
    setSubmitOk(false);
    if (!selectedRunId || !selectedDriverId) {
      setSubmitError("Select a run and a driver on shift.");
      return;
    }
    setSubmitting(true);
    try {
      await postTransporterManualAssign(getToken, tid, {
        runId: selectedRunId,
        driverId: selectedDriverId,
      });
      setSubmitOk(true);
      const r = await fetchTransporterEligibleDispatchRuns(getToken, tid);
      setRuns(r.runs);
      setSelectedRunId("");
      setSelectedDriverId("");
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : "manual_assign_failed");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <section className="space-y-6">
      <div>
        <p className="text-sm text-gray">
          Reassign a run to another driver before a manifest is created (while the run is still proposed or assigned and
          the manifest is pending). The driver you pick must have an <strong className="font-medium text-gray">open
          shift</strong>.
        </p>
      </div>

      {loadError ? <p className="text-sm text-red">Could not load dispatch data. Try refreshing.</p> : null}
      {!loadError && runs === null ? <p className="text-sm text-gray">Loading…</p> : null}

      {runs && runs.length === 0 && !loadError ? (
        <p className="text-sm text-gray">No runs are eligible for manual dispatch right now.</p>
      ) : null}

      {runs && runs.length > 0 && driversOnShift.length === 0 && !loadError ? (
        <p className="text-sm text-amber-900">
          No drivers are currently on shift — open a shift in the driver app before assigning.
        </p>
      ) : null}

      {runs && runs.length > 0 && driversOnShift.length > 0 ? (
        <form onSubmit={onSubmit} className="td-surface max-w-xl space-y-4 p-6">
          <label className="block text-xs text-gray">
            Eligible run
            <select
              className="input-field mt-1"
              value={selectedRunId}
              onChange={(e) => setSelectedRunId(e.target.value)}
            >
              <option value="">Select…</option>
              {runs.map((r) => (
                <option key={r.id} value={r.id}>
                  {runLabel(r)}
                </option>
              ))}
            </select>
          </label>
          <label className="block text-xs text-gray">
            Driver (on shift)
            <select
              className="input-field mt-1"
              value={selectedDriverId}
              onChange={(e) => setSelectedDriverId(e.target.value)}
            >
              <option value="">Select…</option>
              {driversOnShift.map((d) => (
                <option key={d.driverId} value={d.driverId}>
                  {d.fullName}
                </option>
              ))}
            </select>
          </label>
          {submitError ? <p className="text-sm text-red">{submitError}</p> : null}
          {submitOk ? (
            <p className="text-sm text-success">Assignment saved. Accept timer was reset if configured.</p>
          ) : null}
          <button
            type="submit"
            disabled={submitting}
            className="btn-primary disabled:opacity-50"
          >
            {submitting ? "Saving…" : "Assign driver"}
          </button>
        </form>
      ) : null}
    </section>
  );
}
